import axios from 'axios';

const API_URL = '/api';

// Test mode flag - fetched from backend using singleton pattern
let isDebugMode = false;
let debugModePromise = null;

// Helper function to open PDFs in new tab or same tab if blocked
const openPdfInNewTab = (blob) => {
  const url = window.URL.createObjectURL(blob);
  const newWindow = window.open(url, '_blank');
  
  // Check if popup was blocked
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    // Popup blocked - open in same tab instead
    console.warn('Popup blocked - opening PDF in same tab');
    window.location.href = url;
    // Note: Clean up happens when page unloads
  } else {
    // Clean up the URL after a delay to allow the browser to open it
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }
};

// Helper function to safely parse JSON error responses from blobs
const parseJsonErrorFromBlob = async (blob, defaultErrorMsg) => {
  try {
    const text = await blob.text();
    console.error('[DEBUG] Error en respuesta (JSON):', text);
    try {
      const errorData = JSON.parse(text);
      return errorData.message || defaultErrorMsg;
    } catch (parseError) {
      return `${defaultErrorMsg}: respuesta inválida del servidor`;
    }
  } catch (err) {
    return `${defaultErrorMsg}: no se pudo leer la respuesta`;
  }
};

// Helper function to check if a successful blob response is actually a JSON error
const checkBlobResponseForError = async (responseData, defaultErrorMsg) => {
  if (responseData && responseData.type === 'application/json') {
    const errorMsg = await parseJsonErrorFromBlob(responseData, defaultErrorMsg);
    throw new Error(errorMsg);
  }
};

// Helper function to handle blob errors in catch blocks
const handleBlobError = async (error, defaultErrorMsg) => {
  if (error.response?.data instanceof Blob && error.response?.data.type === 'application/json') {
    const errorMsg = await parseJsonErrorFromBlob(error.response.data, defaultErrorMsg);
    throw new Error(errorMsg);
  }
  throw error;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const user = localStorage.getItem('user');
    if (user) {
      const { token } = JSON.parse(user);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper to extract error details for display with comprehensive debug info
export const extractErrorDetails = (error, defaultMessage = 'Error desconocido') => {
  let message = defaultMessage;
  let errorTrace = null;
  let debugInfo = null;

  if (error.response?.data) {
    const data = error.response.data;
    message = data.message || defaultMessage;
    errorTrace = data.errorTrace || null;
    
    // Capture comprehensive debug information
    let requestData = null;
    if (error.config?.data) {
      try {
        requestData = JSON.parse(error.config.data);
      } catch (parseError) {
        requestData = error.config.data; // Use raw data if parsing fails
      }
    }
    
    debugInfo = {
      status: error.response.status,
      statusText: error.response.statusText,
      url: error.config?.url || 'Unknown URL',
      method: error.config?.method?.toUpperCase() || 'Unknown Method',
      requestData: requestData,
      responseData: data,
      timestamp: new Date().toISOString()
    };
  } else if (error.message) {
    message = error.message;
    debugInfo = {
      errorMessage: error.message,
      errorName: error.name,
      timestamp: new Date().toISOString()
    };
  }

  return { message, errorTrace, debugInfo, isDebugMode };
};

// Config service to fetch debug mode status using singleton pattern
export const configService = {
  fetchDebugMode: async () => {
    // Use singleton pattern to prevent concurrent requests
    if (debugModePromise) {
      return debugModePromise;
    }
    
    debugModePromise = (async () => {
      try {
        const response = await axios.get(`${API_URL}/config/debug-mode`);
        isDebugMode = response.data.debugMode === true;
        return isDebugMode;
      } catch (err) {
        // If the endpoint fails, assume not in debug mode
        isDebugMode = false;
        return false;
      }
    })();
    
    return debugModePromise;
  },
  
  isDebugMode: () => isDebugMode
};

export const authService = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
};

export const orderService = {
  getAllOrders: async () => {
    const response = await api.get('/orders');
    return response.data;
  },
  
  getOrdersWithFilters: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.orderNumber) params.append('orderNumber', filters.orderNumber);
    if (filters.status) params.append('status', filters.status);
    if (filters.fromDate) params.append('fromDate', filters.fromDate);
    if (filters.toDate) params.append('toDate', filters.toDate);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    if (filters.page !== undefined) params.append('page', filters.page);
    if (filters.size) params.append('size', filters.size);
    
    const response = await api.get(`/orders/filter?${params.toString()}`);
    return response.data;
  },
  
  getMyOrders: async () => {
    const response = await api.get('/orders/my-orders');
    return response.data;
  },
  
  getMyOrdersWithFilters: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.orderNumber) params.append('orderNumber', filters.orderNumber);
    if (filters.status) params.append('status', filters.status);
    if (filters.fromDate) params.append('fromDate', filters.fromDate);
    if (filters.toDate) params.append('toDate', filters.toDate);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    if (filters.page !== undefined) params.append('page', filters.page);
    if (filters.size) params.append('size', filters.size);
    
    const response = await api.get(`/orders/my-orders/filter?${params.toString()}`);
    return response.data;
  },
  
  getOrderHistory: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/history`);
    return response.data;
  },
  
  createOrder: async (orderData) => {
    const response = await api.post('/orders', orderData);
    return response.data;
  },

  updateOrder: async (id, orderData) => {
    const response = await api.put(`/orders/${id}`, orderData);
    return response.data;
  },

  submitOrder: async (id) => {
    const response = await api.post(`/orders/${id}/submit`);
    return response.data;
  },

  updateOrderStatus: async (id, status, confirmedItems = null) => {
    const requestBody = { status };
    if (confirmedItems) {
      requestBody.confirmedItems = confirmedItems;
    }
    const response = await api.put(`/orders/${id}/status`, requestBody);
    return response.data;
  },
  
  downloadOrderPdf: async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}/pdf`, {
        responseType: 'blob'
      });
      
      await checkBlobResponseForError(response.data, 'Error al descargar el PDF');
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      openPdfInNewTab(blob);
    } catch (err) {
      console.error('[DEBUG] Error descargando PDF:', err);
      await handleBlobError(err, 'Error al descargar el PDF');
    }
  },
  
  exportOrdersToExcel: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      
      const url = `/orders/export/excel?${params.toString()}`;
      console.log('[DEBUG] Exportando órdenes a Excel:', { url, filters });
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      console.log('[DEBUG] Respuesta recibida:', { 
        status: response.status, 
        contentType: response.headers['content-type'],
        size: response.data.size 
      });
      
      await checkBlobResponseForError(response.data, 'Error al exportar a Excel');
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'ordenes.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      console.log('[DEBUG] Archivo descargado exitosamente');
    } catch (err) {
      console.error('[DEBUG] Error completo al exportar:', err);
      console.error('[DEBUG] Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      await handleBlobError(err, 'Error al exportar a Excel');
    }
  },
  
  exportOrdersToCsv: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      
      const url = `/orders/export/csv?${params.toString()}`;
      console.log('[DEBUG] Exportando órdenes a CSV:', { url, filters });
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      console.log('[DEBUG] Respuesta recibida:', { 
        status: response.status, 
        contentType: response.headers['content-type'],
        size: response.data.size 
      });
      
      await checkBlobResponseForError(response.data, 'Error al exportar a CSV');
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'ordenes.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      console.log('[DEBUG] Archivo descargado exitosamente');
    } catch (err) {
      console.error('[DEBUG] Error completo al exportar:', err);
      console.error('[DEBUG] Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      await handleBlobError(err, 'Error al exportar a CSV');
    }
  },
  
  exportMyOrdersToExcel: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      
      const url = `/orders/my-orders/export/excel?${params.toString()}`;
      console.log('[DEBUG] Exportando mis órdenes a Excel:', { url, filters });
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      console.log('[DEBUG] Respuesta recibida:', { 
        status: response.status, 
        contentType: response.headers['content-type'],
        size: response.data.size 
      });
      
      await checkBlobResponseForError(response.data, 'Error al exportar a Excel');
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'mis-ordenes.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      console.log('[DEBUG] Archivo descargado exitosamente');
    } catch (err) {
      console.error('[DEBUG] Error completo al exportar:', err);
      console.error('[DEBUG] Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      await handleBlobError(err, 'Error al exportar a Excel');
    }
  },
  
  exportMyOrdersToCsv: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      
      const url = `/orders/my-orders/export/csv?${params.toString()}`;
      console.log('[DEBUG] Exportando mis órdenes a CSV:', { url, filters });
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      console.log('[DEBUG] Respuesta recibida:', { 
        status: response.status, 
        contentType: response.headers['content-type'],
        size: response.data.size 
      });
      
      await checkBlobResponseForError(response.data, 'Error al exportar a CSV');
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'mis-ordenes.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      console.log('[DEBUG] Archivo descargado exitosamente');
    } catch (err) {
      console.error('[DEBUG] Error completo al exportar:', err);
      console.error('[DEBUG] Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      await handleBlobError(err, 'Error al exportar a CSV');
    }
  },
};

export const returnService = {
  getAllReturns: async () => {
    const response = await api.get('/returns');
    return response.data;
  },
  
  getReturnsWithFilters: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.orderNumber) params.append('orderNumber', filters.orderNumber);
    if (filters.status) params.append('status', filters.status);
    if (filters.fromDate) params.append('fromDate', filters.fromDate);
    if (filters.toDate) params.append('toDate', filters.toDate);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    if (filters.page !== undefined) params.append('page', filters.page);
    if (filters.size !== undefined) params.append('size', filters.size);
    
    const response = await api.get(`/returns/filter?${params.toString()}`);
    return response.data;
  },
  
  getMyReturns: async () => {
    const response = await api.get('/returns/my-returns');
    return response.data;
  },
  
  getMyReturnsWithFilters: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.orderNumber) params.append('orderNumber', filters.orderNumber);
    if (filters.status) params.append('status', filters.status);
    if (filters.fromDate) params.append('fromDate', filters.fromDate);
    if (filters.toDate) params.append('toDate', filters.toDate);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    if (filters.page !== undefined) params.append('page', filters.page);
    if (filters.size !== undefined) params.append('size', filters.size);
    
    const response = await api.get(`/returns/my-returns/filter?${params.toString()}`);
    return response.data;
  },
  
  getReturnById: async (id) => {
    const response = await api.get(`/returns/${id}`);
    return response.data;
  },
  
  createReturn: async (returnData) => {
    const response = await api.post('/returns', returnData);
    return response.data;
  },
  
  updateReturnStatus: async (id, statusData) => {
    const response = await api.put(`/returns/${id}/status`, statusData);
    return response.data;
  },
  
  getReturnHistory: async (id) => {
    const response = await api.get(`/returns/${id}/history`);
    return response.data;
  },
  
  downloadReturnPdf: async (id) => {
    try {
      const response = await api.get(`/returns/${id}/pdf`, {
        responseType: 'blob'
      });
      
      await checkBlobResponseForError(response.data, 'Error al descargar el PDF');
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      openPdfInNewTab(blob);
    } catch (err) {
      console.error('[DEBUG] Error descargando PDF:', err);
      await handleBlobError(err, 'Error al descargar el PDF');
    }
  },
  
  exportReturnsToExcel: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      
      const response = await api.get(`/returns/export/excel?${params.toString()}`, {
        responseType: 'blob'
      });
      
      await checkBlobResponseForError(response.data, 'Error al exportar a Excel');
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'devoluciones.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[DEBUG] Error exportando devoluciones a Excel:', err);
      await handleBlobError(err, 'Error al exportar a Excel');
    }
  },
  
  exportReturnsToCsv: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      
      const response = await api.get(`/returns/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      await checkBlobResponseForError(response.data, 'Error al exportar a CSV');
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'devoluciones.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[DEBUG] Error exportando devoluciones a CSV:', err);
      await handleBlobError(err, 'Error al exportar a CSV');
    }
  },
  
  exportMyReturnsToExcel: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      
      const response = await api.get(`/returns/my-returns/export/excel?${params.toString()}`, {
        responseType: 'blob'
      });
      
      await checkBlobResponseForError(response.data, 'Error al exportar a Excel');
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'mis-devoluciones.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[DEBUG] Error exportando mis devoluciones a Excel:', err);
      await handleBlobError(err, 'Error al exportar a Excel');
    }
  },
  
  exportMyReturnsToCsv: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      
      const response = await api.get(`/returns/my-returns/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      await checkBlobResponseForError(response.data, 'Error al exportar a CSV');
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'mis-devoluciones.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[DEBUG] Error exportando mis devoluciones a CSV:', err);
      await handleBlobError(err, 'Error al exportar a CSV');
    }
  },
};

export const productService = {
  getAllProducts: async () => {
    const response = await api.get('/products');
    return response.data;
  },
  
  getAllActiveProducts: async () => {
    const response = await api.get('/products/active');
    return response.data;
  },
  
  getProductsPaginated: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.page !== undefined) params.append('page', filters.page);
    if (filters.size !== undefined) params.append('size', filters.size);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    
    const response = await api.get(`/products/paginated?${params.toString()}`);
    return response.data;
  },
  
  getProductById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },
  
  getProductByBarcode: async (barcode) => {
    const response = await api.get(`/products/barcode/${barcode}`);
    return response.data;
  },
  
  searchProducts: async (query) => {
    const response = await api.get(`/products/search?query=${encodeURIComponent(query)}`);
    return response.data;
  },
  
  filterProducts: async (query) => {
    const response = await api.get(`/products/filter?query=${encodeURIComponent(query)}`);
    return response.data;
  },
  
  createProduct: async (productData) => {
    const response = await api.post('/products', productData);
    return response.data;
  },

  quickCreateProduct: async (nombre, descripcion) => {
    const response = await api.post('/products/quick-create', { nombre, descripcion });
    return response.data;
  },

  updateProduct: async (id, productData) => {
    const response = await api.put(`/products/${id}`, productData);
    return response.data;
  },
  
  deleteProduct: async (id) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  },

  importProducts: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/products/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export const userService = {
  getAllUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  
  getUserById: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  
  createUser: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },
  
  updateUser: async (id, userData) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },
  
  deleteUser: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
  
  toggleUserStatus: async (id) => {
    const response = await api.put(`/users/${id}/toggle-status`);
    return response.data;
  },
};

export const supermercadoService = {
  getAllSupermercados: async () => {
    const response = await api.get('/supermercados');
    return response.data;
  },
  
  getActiveSupermercados: async () => {
    const response = await api.get('/supermercados/active');
    return response.data;
  },
  
  getSupermercadoById: async (id) => {
    const response = await api.get(`/supermercados/${id}`);
    return response.data;
  },
  
  createSupermercado: async (data) => {
    const response = await api.post('/supermercados', data);
    return response.data;
  },
  
  updateSupermercado: async (id, data) => {
    const response = await api.put(`/supermercados/${id}`, data);
    return response.data;
  },
  
  deleteSupermercado: async (id) => {
    const response = await api.delete(`/supermercados/${id}`);
    return response.data;
  },
};

export const proveedorService = {
  getAllProveedores: async () => {
    const response = await api.get('/proveedores');
    return response.data;
  },
  
  getActiveProveedores: async () => {
    const response = await api.get('/proveedores/active');
    return response.data;
  },
  
  getProveedorById: async (id) => {
    const response = await api.get(`/proveedores/${id}`);
    return response.data;
  },
  
  createProveedor: async (data) => {
    const response = await api.post('/proveedores', data);
    return response.data;
  },
  
  updateProveedor: async (id, data) => {
    const response = await api.put(`/proveedores/${id}`, data);
    return response.data;
  },
  
  deleteProveedor: async (id) => {
    const response = await api.delete(`/proveedores/${id}`);
    return response.data;
  },
};

export const reportService = {
  // Deposito reports
  getOrdersBySupermercadoReport: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.supermercadoId) params.append('supermercadoId', filters.supermercadoId);
    if (filters.status) params.append('status', filters.status);
    if (filters.fromDate) params.append('fromDate', filters.fromDate);
    if (filters.toDate) params.append('toDate', filters.toDate);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    if (filters.page !== undefined) params.append('page', filters.page);
    if (filters.size) params.append('size', filters.size);
    
    const response = await api.get(`/reports/orders-by-supermercado?${params.toString()}`);
    return response.data;
  },

  // Supermercado reports
  getProductOrderingFrequency: async (sortBy = 'least_recent') => {
    const response = await api.get(`/reports/product-frequency?sortBy=${sortBy}`);
    return response.data;
  },
  
  getProductOrderHistory: async (productId, fromDate = null, toDate = null) => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const response = await api.get(`/reports/product-history/${productId}?${params.toString()}`);
    return response.data;
  },
  
  // Export functions
  exportOrdersBySupermercadoToExcel: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.supermercadoId) params.append('supermercadoId', filters.supermercadoId);
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      
      const url = `/reports/orders-by-supermercado/export/excel?${params.toString()}`;
      console.log('[DEBUG] Exportando reporte de órdenes por supermercado a Excel:', { url, filters });
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      console.log('[DEBUG] Respuesta recibida:', { 
        status: response.status, 
        contentType: response.headers['content-type'],
        size: response.data.size 
      });
      
      // Check if response is an error (JSON) instead of a blob
      if (response.data && response.data.type === 'application/json') {
        const errorMsg = await parseJsonErrorFromBlob(response.data, 'Error al exportar a Excel');
        throw new Error(errorMsg);
      }
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'ordenes-por-supermercado.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      console.log('[DEBUG] Archivo descargado exitosamente');
    } catch (err) {
      console.error('[DEBUG] Error completo al exportar:', err);
      console.error('[DEBUG] Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      throw err;
    }
  },
  
  exportOrdersBySupermercadoToCsv: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.supermercadoId) params.append('supermercadoId', filters.supermercadoId);
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      
      const url = `/reports/orders-by-supermercado/export/csv?${params.toString()}`;
      console.log('[DEBUG] Exportando reporte de órdenes por supermercado a CSV:', { url, filters });
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      console.log('[DEBUG] Respuesta recibida:', { 
        status: response.status, 
        contentType: response.headers['content-type'],
        size: response.data.size 
      });
      
      // Check if response is an error (JSON) instead of a blob
      if (response.data && response.data.type === 'application/json') {
        const errorMsg = await parseJsonErrorFromBlob(response.data, 'Error al exportar a CSV');
        throw new Error(errorMsg);
      }
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'ordenes-por-supermercado.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      console.log('[DEBUG] Archivo descargado exitosamente');
    } catch (err) {
      console.error('[DEBUG] Error completo al exportar:', err);
      console.error('[DEBUG] Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      throw err;
    }
  },
  
  exportProductFrequencyToExcel: async (sortBy = 'least_recent') => {
    const response = await api.get(`/reports/product-frequency/export/excel?sortBy=${sortBy}`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'frecuencia-pedidos.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
  
  exportProductFrequencyToCsv: async (sortBy = 'least_recent') => {
    const response = await api.get(`/reports/product-frequency/export/csv?sortBy=${sortBy}`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'frecuencia-pedidos.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
  
  exportProductHistoryToExcel: async (productId, fromDate = null, toDate = null) => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const response = await api.get(`/reports/product-history/${productId}/export/excel?${params.toString()}`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historial-producto-${productId}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
  
  exportProductHistoryToCsv: async (productId, fromDate = null, toDate = null) => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const response = await api.get(`/reports/product-history/${productId}/export/csv?${params.toString()}`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historial-producto-${productId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
  
  // PDF Export functions
  exportOrdersBySupermercadoToPdf: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.supermercadoId) params.append('supermercadoId', filters.supermercadoId);
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      
      const url = `/reports/orders-by-supermercado/export/pdf?${params.toString()}`;
      console.log('[DEBUG] Exportando reporte de órdenes por supermercado a PDF:', { url, filters });
      
      const response = await api.get(url, {
        responseType: 'blob'
      });
      
      console.log('[DEBUG] Respuesta recibida:', { 
        status: response.status, 
        contentType: response.headers['content-type'],
        size: response.data.size 
      });
      
      // Check if response is an error (JSON) instead of a blob
      if (response.data && response.data.type === 'application/json') {
        const errorMsg = await parseJsonErrorFromBlob(response.data, 'Error al exportar a PDF');
        throw new Error(errorMsg);
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      openPdfInNewTab(blob);
      
      console.log('[DEBUG] PDF abierto exitosamente');
    } catch (err) {
      console.error('[DEBUG] Error completo al exportar:', err);
      console.error('[DEBUG] Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      throw err;
    }
  },
  
  // Supermercado Reports PDF Export
  exportProductFrequencyToPdf: async (sortBy = 'least_recent') => {
    const response = await api.get(`/reports/product-frequency/export/pdf?sortBy=${sortBy}`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    openPdfInNewTab(blob);
  },
  
  exportProductHistoryToPdf: async (productId, fromDate = null, toDate = null) => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const response = await api.get(`/reports/product-history/${productId}/export/pdf?${params.toString()}`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    openPdfInNewTab(blob);
  },
};

export const databaseSchemaService = {
  getSchema: async () => {
    const response = await api.get('/database-schema');
    return response.data;
  },
};

export const supplierOrderService = {
  getAllSupplierOrders: async () => {
    const response = await api.get('/supplier-orders');
    return response.data;
  },
  
  getSupplierOrdersWithFilters: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.orderNumber) params.append('orderNumber', filters.orderNumber);
    if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
    if (filters.status) params.append('status', filters.status);
    if (filters.fromDate) params.append('fromDate', filters.fromDate);
    if (filters.toDate) params.append('toDate', filters.toDate);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
    if (filters.page !== undefined) params.append('page', filters.page);
    if (filters.size !== undefined) params.append('size', filters.size);
    
    const response = await api.get(`/supplier-orders/filter?${params.toString()}`);
    return response.data;
  },
  
  getSupplierOrderById: async (id) => {
    const response = await api.get(`/supplier-orders/${id}`);
    return response.data;
  },
  
  createSupplierOrder: async (orderData) => {
    const response = await api.post('/supplier-orders', orderData);
    return response.data;
  },
  
  updateSupplierOrderStatus: async (id, statusData) => {
    const response = await api.put(`/supplier-orders/${id}/status`, statusData);
    return response.data;
  },
  
  deleteSupplierOrder: async (id) => {
    const response = await api.delete(`/supplier-orders/${id}`);
    return response.data;
  },
  
  getSupplierOrderHistory: async (id) => {
    const response = await api.get(`/supplier-orders/${id}/history`);
    return response.data;
  },
  
  downloadSupplierOrderPdf: async (id) => {
    try {
      const response = await api.get(`/supplier-orders/${id}/pdf`, {
        responseType: 'blob'
      });
      
      await checkBlobResponseForError(response.data, 'Error al descargar el PDF');
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      openPdfInNewTab(blob);
    } catch (err) {
      await handleBlobError(err, 'Error al descargar el PDF');
    }
  },
  
  exportSupplierOrdersToExcel: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      if (filters.page !== undefined) params.append('page', filters.page);
      if (filters.size !== undefined) params.append('size', filters.size);
      
      const response = await api.get(`/supplier-orders/export/excel?${params.toString()}`, {
        responseType: 'blob'
      });
      
      await checkBlobResponseForError(response.data, 'Error al exportar a Excel');
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'ordenes-proveedor.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      await handleBlobError(err, 'Error al exportar a Excel');
    }
  },
  
  exportSupplierOrdersToCsv: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.proveedorId) params.append('proveedorId', filters.proveedorId);
      if (filters.status) params.append('status', filters.status);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
      if (filters.page !== undefined) params.append('page', filters.page);
      if (filters.size !== undefined) params.append('size', filters.size);
      
      const response = await api.get(`/supplier-orders/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      await checkBlobResponseForError(response.data, 'Error al exportar a CSV');
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'ordenes-proveedor.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      await handleBlobError(err, 'Error al exportar a CSV');
    }
  },
};

export default api;
